import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Upload, Loader2, Database, CheckCircle2, ExternalLink, Search,
} from "lucide-react";
import SkipPasteDialog from "@/components/SkipPasteDialog";
import { fileToCsv, FILE_ACCEPT } from "@/lib/fileToCsv";

export default function SkipTrace() {
  const [stats, setStats] = useState({ total_cached: 0, with_phone: 0 });
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [lookupAddr, setLookupAddr] = useState("");
  const [lookupOwner, setLookupOwner] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(false);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/skip-trace/stats");
      setStats(data);
    } catch { /* silent */ }
  };
  useEffect(() => { loadStats(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      setCsv(await fileToCsv(f));
      toast.success(`Loaded ${f.name}`);
    } catch (err) {
      toast.error(err?.message || "Couldn't read the file");
    }
    e.target.value = "";
  };

  const runImport = async () => {
    if (!csv.trim()) return toast.error("Paste or upload a CSV first");
    setBusy(true);
    try {
      const { data } = await api.post("/skip-trace/bulk-import",
        { csv_text: csv });
      toast.success(
        `Imported ${data.imported} rows · ${data.leads_updated} leads updated`
      );
      setCsv("");
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const doLookup = async () => {
    if (!lookupAddr.trim()) return toast.error("Enter an address");
    setLookupResult(null);
    try {
      const { data } = await api.get("/skip-trace/lookup", {
        params: { address: lookupAddr.trim(), owner_name: lookupOwner.trim() || undefined },
      });
      setLookupResult(data);
    } catch { toast.error("Lookup failed"); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
          Skip Trace
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Store every phone, email, and relative you find so future lookups
          on the same house are free forever. Compatible with any provider —
          Skip Genie, PropStream, REISkip, TruePeopleSearch, or manual research.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cached addresses</div>
          <div className="text-2xl font-display font-bold mt-1" data-testid="skip-stat-total">
            {stats.total_cached}
          </div>
        </div>
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">With phone</div>
          <div className="text-2xl font-display font-bold mt-1" data-testid="skip-stat-phone">
            {stats.with_phone}
          </div>
        </div>
        <div className="card-flat p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Coverage</div>
          <div className="text-2xl font-display font-bold mt-1">
            {stats.total_cached === 0 ? "—"
              : Math.round((stats.with_phone / stats.total_cached) * 100) + "%"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Lookup */}
        <div className="card-flat p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Search size={16} />
            <div className="font-display text-lg font-bold">Look up an address</div>
          </div>
          <div>
            <Label className="text-xs">Address</Label>
            <input
              value={lookupAddr}
              onChange={(e) => setLookupAddr(e.target.value)}
              placeholder="123 Palm Ave Miami FL 33101"
              className="w-full h-10 rounded-md border border-[var(--app-line)] px-3 text-sm mt-1"
              data-testid="skip-lookup-address"
            />
          </div>
          <div>
            <Label className="text-xs">Owner (optional)</Label>
            <input
              value={lookupOwner}
              onChange={(e) => setLookupOwner(e.target.value)}
              placeholder="John Smith"
              className="w-full h-10 rounded-md border border-[var(--app-line)] px-3 text-sm mt-1"
              data-testid="skip-lookup-owner"
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={doLookup}
              disabled={!lookupAddr.trim()}
              className="rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
              data-testid="skip-lookup-btn"
            >
              <Search size={13} className="mr-2" /> Check cache
            </Button>
            <Button
              onClick={() => setPasteOpen(true)}
              variant="outline"
              className="rounded-full"
              data-testid="skip-add-manual-btn"
            >
              + Add manually
            </Button>
          </div>

          {lookupResult && (
            <div
              className={`rounded-lg p-3 text-xs border ${
                lookupResult.found
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-amber-200 bg-amber-50"
              }`}
              data-testid="skip-lookup-result"
            >
              {lookupResult.found ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle2 size={13} className="text-emerald-700" />
                    Cached — source: {lookupResult.source}
                  </div>
                  <div><b>Phones:</b> <span className="font-mono">{(lookupResult.data.phones || []).join(", ") || "—"}</span></div>
                  <div><b>Emails:</b> <span className="font-mono">{(lookupResult.data.emails || []).join(", ") || "—"}</span></div>
                  <div><b>Relatives:</b> {(lookupResult.data.relatives || []).join(", ") || "—"}</div>
                  <div className="text-[10px] text-muted-foreground">Last updated: {lookupResult.last_updated_at}</div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div>Not in cache yet. Look it up on{" "}
                    <a href={`https://www.truepeoplesearch.com/results?citystatezip=${encodeURIComponent(lookupAddr)}${lookupOwner ? `&name=${encodeURIComponent(lookupOwner)}` : ""}`}
                       target="_blank" rel="noreferrer"
                       className="text-[var(--app-accent)] hover:underline"
                       data-testid="skip-lookup-tps">
                      TruePeopleSearch <ExternalLink size={9} className="inline" />
                    </a>
                    {" "}and paste the results.
                  </div>
                  <Button
                    onClick={() => setPasteOpen(true)}
                    className="rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
                  >
                    + Add to cache
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bulk import */}
        <div className="card-flat p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Database size={16} />
            <div className="font-display text-lg font-bold">Bulk CSV Import</div>
          </div>
          <div className="text-xs text-muted-foreground">
            Skip your list on any provider (Skip Genie, REISkip, PropStream…),
            download their CSV, and drop it here. We recognize columns like{" "}
            <code className="text-[10px] px-1 bg-neutral-100 rounded">address</code>,{" "}
            <code className="text-[10px] px-1 bg-neutral-100 rounded">owner_name</code>,{" "}
            <code className="text-[10px] px-1 bg-neutral-100 rounded">phone1..phone5</code>,{" "}
            <code className="text-[10px] px-1 bg-neutral-100 rounded">email1..3</code>,{" "}
            <code className="text-[10px] px-1 bg-neutral-100 rounded">relative1..3</code>.
          </div>
          <Textarea
            rows={8}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder="address,owner_name,phone1,phone2,email1,relative1&#10;123 Palm Ave Miami FL,John Doe,(305) 555-1111,(305) 555-2222,john@ex.com,Jane Doe"
            className="mt-1 font-mono text-xs"
            data-testid="skip-bulk-csv"
          />
          <label className="inline-flex items-center text-[11px] text-muted-foreground cursor-pointer hover:underline">
            <Upload size={11} className="mr-1" /> Upload CSV / XLSX file
            <input type="file" accept={FILE_ACCEPT} onChange={onFile} className="hidden"
                   data-testid="skip-bulk-file" />
          </label>
          <div>
            <Button
              onClick={runImport}
              disabled={busy || !csv.trim()}
              className="rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
              data-testid="skip-bulk-submit"
            >
              {busy ? <Loader2 size={13} className="animate-spin mr-2" /> : <Upload size={13} className="mr-2" />}
              Import + Cache
            </Button>
          </div>
        </div>
      </div>

      <div className="card-flat p-4 text-xs text-muted-foreground">
        <div className="font-medium text-[var(--app-text)] mb-1">Why caching matters</div>
        Every phone number you save here is available to <b>every future skip
        lookup on the same house</b>, across leads, properties, D4D pins, and
        Data-List imports — so you never pay twice. When you plug in a
        SkipReach API key later, misses will auto-fill this same cache.
      </div>

      <SkipPasteDialog
        open={pasteOpen}
        onOpenChange={setPasteOpen}
        address={lookupAddr}
        ownerName={lookupOwner}
        onSaved={() => { loadStats(); doLookup(); }}
      />
    </div>
  );
}
